// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Base64Binary from 'base64-arraybuffer';

// Import Material UI
import CancelIcon from '@material-ui/icons/Cancel';
import { green } from '@material-ui/core/colors';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { Select, MenuItem, TextField, FormControlLabel, Checkbox, RadioGroup, Radio, Switch, Typography, Box, Tabs } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import { CircularProgress } from '@material-ui/core';

// Import Pages
import Confirm from "../DataTable/DeleteConfirm";

// Import Helpers
import { getExtension, CheckIfFlowUpdated } from '../../helpers/helper';

// Import Recoil
import { initialActions, saveAvailable, changedFields, alertState } from "../../recoil-store/recoil-state";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

// Import Services
import MateDriveService from "../../services/MateDriveService/MateDriveService";

// Import CSS
import 'draft-js-prism-decorator';
import 'prismjs/components/prism-typescript';
import 'prismjs/themes/prism.css';


export function Dropdown({ action, inputName, options, multiple, isRequired, setField, fullWidth = true, setRefresh, refresh = undefined }) {
    const { t } = useTranslation();
    const [dropdownValue, setDropdownValue] = useState("");
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

    useEffect(() => {
        if (action.fields) {
            if (!action.fields[inputName]) {
                action.fields[inputName] = { isRequired };
            }
            if (action.fields[inputName].value) {
                setDropdownValue(action.fields[inputName].value);
            } else {
                action.fields[inputName].value = options.defaultValue;
                setDropdownValue(options.defaultValue);
            }
        } else {
            if (!action[inputName]) {
                action[inputName] = { isRequired };
            }
            if (action[inputName]?.value) {
                setDropdownValue(action[inputName].value);
            } else {
                action[inputName].value = options.defaultValue;
                setDropdownValue(options.defaultValue);
            }
        }

    }, [action, refresh]);

    const handleChangeState = (event) => {
        if (refresh !== null && refresh !== undefined) {
            setRefresh(!refresh);
        }
        setDropdownValue(event.target.value);

        if (action.fields) {
            const field = initialActionsData[action.id]?.fields[inputName];
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated(inputName, action, oldValue, event.target.value, setSaveAvailable, setChangeFields, changedFieldsState);

            action.fields[inputName].value = event.target.value;
            if (setField) {
                setField(inputName, { value: event.target.value });
            }
        } else {
            action[inputName].value = event.target.value;
            if (setField) {
                setField(inputName, { value: event.target.value });
            }
        }

    };

    return (
        <>
            {/* <div>{(isRequired === true) && <small style={{color: '#D75E5E'}}>* Required</small>}</div> */}
            <Select value={dropdownValue} onChange={handleChangeState} variant="outlined"
                multiple={multiple} fullWidth={fullWidth} style={{ paddingLeft: "5px", height: 34, fontSize: 12, color: "#8F94A6", fontWeight: "800", borderRadius: 2, width: '90%' }}>
                {options.data.map((item, itemI) =>
                    <MenuItem style={{ fontSize: 12, color: "#8F94A6", fontWeight: "800" }} key={itemI} value={item.value}>{t(item.label)}</MenuItem>
                )}
            </Select>
        </>
    );
}

export function FileInput({ action, inputName, index, options, isRequired, clear, disabled, isSize = false, hideRequired, onChangeFile }) {
    const [inputValue, setInputValue] = useState('');
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const setAlert = useSetRecoilState(alertState);
    const { t } = useTranslation();

    useEffect(() => {
        if (action.fields) {
            if (!action.fields[inputName]) {
                action.fields[inputName] = { type: 'FILE', isRequired };
            }
            if (action.fields[inputName].value) {
                setInputValue(action.fields[inputName].value);
            } else {
                setInputValue('');
            }
        } else {
            if (!action[inputName]) {
                action[inputName] = { type: 'FILE', isRequired };
            }
            if (action[inputName].value) {
                setInputValue(action[inputName].value);
            } else {
                setInputValue('');
            }
        }
    }, [action]);
    const handleChange = (event) => {
        let ext = getExtension(event.target.value);
        if (event.target.files[0].size > 1200000) {
            return setAlert(alert => ({
                ...alert,
                show: true,
                message: t("FLOW-MESSAGES.LIBRARY.EXCEL.FILE-BIGGER-THAN-ALLOWED-SIZE-10MB"),
                severity: 'error'
            }))
        }
        if (options?.checkExtension) {
            if (!options.checkExtension(ext)) {
                return false;
            }
        }
        const reader = new FileReader();
        reader.readAsArrayBuffer(event.target.files[0]);

        reader.onload = (e) => {
            const arrayBuffer = new Uint8Array(e.target.result);
            const base64convert = Base64Binary.encode(arrayBuffer);
            if (action.fields) {
                action.fields[inputName].byteValue = base64convert;
            } else {
                action[inputName].byteValue = base64convert;
            }
        };

        const fullName = event.target.files[0].name;
        if (action.fields) {
            const field = initialActionsData[action.id]?.fields[inputName];
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated(inputName, action, oldValue, fullName, setSaveAvailable, setChangeFields, changedFieldsState);

            action.fields[inputName].fileName = fullName.split('.').slice(0, -1).join('.');
            action.fields[inputName].extension = ext;
            action.fields[inputName].value = fullName;
        } else {
            action[inputName].fileName = fullName.split('.').slice(0, -1).join('.');
            action[inputName].extension = ext;
            action[inputName].value = fullName;
            if (onChangeFile) {
                onChangeFile();
            }
        }


        //If the "isSize" field is true, fileSize key and value add it
        if (isSize) {
            if (action.fields) {
                action.fields[inputName].fileSize = { value: event.target.files[0].size };
            } else {
                action[inputName].fileSize = { value: event.target.files[0].size };
            }
        }
        setInputValue(fullName);
    };

    const handleClear = () => {
        if (action.fields) {
            const field = initialActionsData[action.id]?.fields[inputName];
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated(inputName, action, oldValue, undefined, setSaveAvailable, setChangeFields, changedFieldsState);
            delete action.fields[inputName].value;
        } else {
            delete action[inputName].value;
        }
        setInputValue(undefined);
    };

    return (
        <>
            <div>{(isRequired && !hideRequired && inputValue.length === 0) && <small style={{ color: '#D75E5E' }}>{t('FLOW-MESSAGES.LIBRARY.GENERAL.REQUIRED')}</small>}</div>
            <div>
                <div className="custom-file config-form" style={{ marginTop: 10, width: '100%' }}>
                    <input type="file" className="custom-file-input" accept={options?.fileType} disabled={disabled} id={`license-customFile${index === undefined ? '' : index}`} onClick={(event) => event.target.value = ''} onChange={handleChange} />
                    <label className="custom-file-label content-overflow" style={{ maxWidth: '100%' }} htmlFor={`license-customFile${index === undefined ? '' : index}`}>{inputValue ? inputValue : t('CONTENT.SETTINGS.GENERAL.CHOOSE')}</label>
                    {(clear && inputValue) &&
                        <CancelIcon onClick={handleClear} style={{ float: 'right', cursor: 'pointer' }} />}
                </div>
                {/* {(clear && inputValue) &&
                    <CancelIcon onClick={handleClear} style={{ float: 'right', cursor: 'pointer' }} />} */}
            </div>
        </>
    );
}

export function FileInputMateDrive({ action, inputName, index, options, isRequired, clear, disabled, isSize = false, hideRequired, onChangeFile, autom_id }) {
    const [inputValue, setInputValue] = useState('');
    const [fileValue, setFileValue] = useState("");
    const [confirm, setConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const setAlert = useSetRecoilState(alertState);
    const { t } = useTranslation();

    useEffect(() => {
        if (action.fields) {
            if (!action.fields[inputName]) {
                action.fields[inputName] = { value: '', isRequired };
            }
            if (action.fields[inputName].value) {
                setInputValue(action.fields[inputName].value);
            } else {
                setInputValue('');
            }
        } else {
            if (!action[inputName]) {
                action[inputName] = { value: '', isRequired };
            }
            if (action[inputName].value) {
                setInputValue(action[inputName].value);
            } else {
                setInputValue('');
            }
        }
    }, [action]);

    const handleChange = async (event) => {
        const maxSize = 50 * 1024 * 1024;
        let ext = getExtension(event.target.value);
        if (event.target.files[0].size > maxSize) {
            return setAlert(alert => ({
                ...alert,
                show: true,
                message: t("FLOW-MESSAGES.LIBRARY.EXCEL.FILE-BIGGER-THAN-ALLOWED-SIZE-50MB"),
                severity: 'error'
            }))
        }
        if (options?.checkExtension) {
            if (!options.checkExtension(ext)) {
                return false;
            }
        }
        const fullName = event.target.files[0].name;
        const result = await MateDriveService.checkFileExists(autom_id, fullName);
        if (result) {
            if (result.status) {
                if (result.message === "FILE-EXISTS") {
                    setFileValue(event.target.files[0])
                    setConfirm(true);
                } else {
                    uploadFile(event.target.files[0]);
                }
            } else {
                if (result.message === "GENERAL.MICROSERVICE-TCP-ERROR") {
                    result.message = "FLOW-MESSAGES.LIBRARY.MATE-DRIVE.CHECK-MIKROSERVICE"
                } else if (result.message.includes("MATE-DRIVE.")) {
                    result.message = t("FLOW-MESSAGES.LIBRARY." + result.message)
                }
                setAlert(alert => ({ ...alert, show: true, message: result.message ? result.message : t('GENERAL.FILE-UPLOAD-ERROR'), severity: 'error' }))
                return;
            }
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }))
            return;
        }


        // const reader = new FileReader();
        // reader.readAsArrayBuffer(event.target.files[0]);

        // reader.onload = (e) => {
        //     const arrayBuffer = new Uint8Array(e.target.result);
        //     const base64convert = Base64Binary.encode(arrayBuffer);
        //     if (action.fields) {
        //         action.fields[inputName].byteValue = base64convert;
        //     } else {
        //         action[inputName].byteValue = base64convert;
        //     }
        // };




    };

    const uploadFile = async (f) => {
        setLoading(true);

        const fullName = f.name;

        const response = await MateDriveService.uploadFile(f, "", autom_id);
        if (response) {
            if (response.status) {
                if (action.fields) {
                    const field = initialActionsData[action.id]?.fields[inputName];
                    const oldValue = !field ? undefined : field.value;
                    CheckIfFlowUpdated(inputName, action, oldValue, fullName, setSaveAvailable, setChangeFields, changedFieldsState);

                    // action.fields[inputName].fileName = fullName.split('.').slice(0, -1).join('.');
                    // action.fields[inputName].extension = ext;
                    action.fields[inputName].value = fullName;
                } else {
                    // action[inputName].fileName = fullName.split('.').slice(0, -1).join('.');
                    // action[inputName].extension = ext;
                    action[inputName].value = fullName;
                }

                //If the "isSize" field is true, fileSize key and value add it
                // if (isSize) {
                //     if (action.fields) {
                //         action.fields[inputName].fileSize = { value: event.target.files[0].size };
                //     } else {
                //         action[inputName].fileSize = { value: event.target.files[0].size };
                //     }
                // }
                setInputValue(fullName);
                if (onChangeFile) {
                    onChangeFile();
                }
            } else {
                if (response.message.includes("MATE-DRIVE.")) {
                    response.message = t("FLOW-MESSAGES.LIBRARY." + response.message)
                }
                setAlert(alert => ({ ...alert, show: true, message: response.message ? response.message : t('GENERAL.FILE-UPLOAD-ERROR'), severity: 'error' }))
            }
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }))
        }
        setLoading(false);
    }

    const handleClear = () => {
        if (action.fields) {
            const field = initialActionsData[action.id]?.fields[inputName];
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated(inputName, action, oldValue, undefined, setSaveAvailable, setChangeFields, changedFieldsState);
            delete action.fields[inputName].value;
        } else {
            delete action[inputName].value;
        }
        setInputValue(undefined);
    };

    return (
        <>
            <div>{(isRequired && !hideRequired && inputValue.length === 0) && <small style={{ color: '#D75E5E' }}>{t('FLOW-MESSAGES.LIBRARY.GENERAL.REQUIRED')}</small>}</div>
            <div>
                <div className="custom-file config-form" style={{ marginTop: 10, width: '100%' }}>
                    <input type="file" className="custom-file-input" accept={options?.fileType} disabled={disabled} id={`license-customFile${index === undefined ? '' : index}`} onClick={(event) => event.target.value = ''} onChange={handleChange} />
                    <label className={`custom-file-label content-overflow ${disabled && "disabled"}`} style={{ maxWidth: '100%' }} htmlFor={`license-customFile${index === undefined ? '' : index}`}>{inputValue ? inputValue : t('CONTENT.SETTINGS.GENERAL.UPLOAD-FILE')}</label>
                    {(clear && inputValue) &&
                        <CancelIcon onClick={handleClear} style={{ float: 'right', cursor: 'pointer' }} />}
                </div>
                {/* {(clear && inputValue) &&
                    <CancelIcon onClick={handleClear} style={{ float: 'right', cursor: 'pointer' }} />} */}
            </div>
            {loading && (
                <Dialog open={true} maxWidth={'sm'}><DialogActions style={{ padding: '30px' }}><CircularProgress /></DialogActions></Dialog >
            )}
            {confirm &&
                <Confirm setOpenDelete={setConfirm}
                    message={t("CONTENT.FLOW.MATE-DRIVE.OVERWRITE-MESSAGE")}
                    deleteFunc={() => uploadFile(fileValue)} />}
        </>
    );
}

export function Checkboxes({ action, inputName, options, onChange, isRequired, style, defaultValue, checkboxStyle, confirmMessage }) {
    const [state, setState] = useState(false);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const [resetConditionConfirm, setResetConditionConfirm] = useState(false);
    // const changedFieldsState = useRecoilValue(changedFields);

    const { t } = useTranslation();

    const handleChange = (event, reset) => {
        if (!event.target.checked && confirmMessage && !reset) {
            setResetConditionConfirm(true);
            return;
        }
        setState(event.target.checked);
        if (action?.fields) {
            const field = initialActionsData[action.id]?.fields[inputName];
            const oldValue = !field ? undefined : field.value;
            if (options.checkedValue) {
                let v = event.target.checked ? options.checkedValue : options.nonCheckedValue
                action.fields[inputName].value = v;

                CheckIfFlowUpdated(inputName, action, oldValue, v, setSaveAvailable, setChangeFields, changedFieldsState);
                if (onChange) {
                    onChange({ value: v });
                }
            } else {
                action.fields[inputName].value = event.target.checked;

                CheckIfFlowUpdated(inputName, action, oldValue, event.target.checked, setSaveAvailable, setChangeFields, changedFieldsState);
                if (onChange) {
                    onChange({ value: event.target.checked });
                }
            }

        } else {
            if (inputName) {
                action[inputName].value = event.target.checked;
            }
            if (onChange) {
                onChange({ value: event.target.checked });
            }
        }

    };

    useEffect(() => {
        if (action?.fields) {
            if (!action.fields[inputName]) {
                action.fields[inputName] = { value: defaultValue !== undefined ? defaultValue : options.nonCheckedValue !== undefined ? options.nonCheckedValue : false };
            }

            if (options.checkedValue) {
                setState(action.fields[inputName].value === options.checkedValue);
            } else {
                setState(action.fields[inputName].value);
            }
        } else {
            if (inputName) {
                if (!action[inputName]) {
                    action[inputName] = { value: false };
                }
                setState(action[inputName].value);
            } else {
                setState(action);
            }
        }

    }, [action]);

    const resetCondition = () => {
        handleChange({ target: { checked: false } }, true);
    }

    return (
        <>
            <FormControlLabel
                control={<Checkbox style={checkboxStyle ? checkboxStyle : {}} checked={state} size="small" color="#C4C4C4" onChange={handleChange} />}
                label={<Typography style={style ? style : { fontSize: "12px", fontWeight: 700, color: "#C4C4C4" }}
                    variant="body2">{t(options.label)}</Typography>}
            />
            {
                resetConditionConfirm && confirmMessage &&
                <Confirm setOpenDelete={setResetConditionConfirm}
                    message={confirmMessage}
                    deleteFunc={resetCondition} />
            }
        </>
    );
}

export function RadioButtons({ action, inputName, options, isRequired, setField }) {
    const [value, setValue] = useState();
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const { t } = useTranslation();

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { type: 'RADIO', value: options.defaultValue, isRequired };
            if (setField) {
                setField(inputName, { type: 'RADIO', value: options.defaultValue, isRequired });
            }
        }
        setValue(action.fields[inputName].value);
    }, [action]);

    const handleChange = (event) => {
        setValue(event.target.value);
        const field = initialActionsData[action.id]?.fields[inputName];
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated(inputName, action, oldValue, event.target.value, setSaveAvailable, setChangeFields, changedFieldsState);

        action.fields[inputName].value = event.target.value;
        if (setField) {
            setField(inputName, { type: 'RADIO', value: event.target.value, isRequired });
        }
    };

    return (
        <RadioGroup value={value ? value : options.defaultValue} onChange={handleChange}>
            {options.data.map((item, itemI) =>
                <FormControlLabel
                    key={itemI} value={item.value}
                    control={<Radio color="#C4C4C4" size='small' />}
                    label={t(item.label)}
                    labelPlacement="end"
                    size="small"
                />
            )}
        </RadioGroup>
    );
}

export function SwitchElement({ item }) {
    return (
        <FormControlLabel
            label={item?.value?.value?.value_labels}
            control={<Switch
                defaultChecked={item?.value?.value?.selected}
                // checked={state.checkedB}
                // onChange={handleChange('checkedB')}
                value="checkedB" color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
            />}
        />
    );
}

export const DateTime = (({ action, inputName, personalization, type = 'date', isRequired }) => {
    const [value, setValue] = useState('');

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { value: '', isRequired };
        } else {
            setValue(action.fields[inputName].value);
        }
    }, [action]);

    const handleChange = (event) => {
        setValue(event.target.value);
        action.fields[inputName].value = event.target.value; //.toLocaleString();
    };
    return (
        <TextField value={value}
            format={`${personalization?.date_format} HH:mm`}
            fullWidth type={type}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
        />
    );
});

export function TextInput({ action, inputName, type = 'number', variant, options }) {
    let defaultValue = options?.defaultValue ? options.defaultValue : '';
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        if (!action.fields[inputName]) {
            action.fields[inputName] = { value: defaultValue };
        } else {
            setValue(action.fields[inputName].value);
        }
    }, [action]);

    const handleChange = (event) => {
        let v = event.target.value;
        if (!options || options?.checkValidation(v)) {
            setValue(v);
            action.fields[inputName].value = v;
        }
    };

    return (
        <TextField value={value}
            type={type} fullWidth
            onChange={handleChange}
            variant={variant}
        />
    );
}

export function TabPanel({ children, value, index }) {
    return (<div>{value === index && (<Box>{children}</Box>)}</div>);
}

export const StyledTabs = withStyles({
    root: {
        minHeight: 'min-content',
        width: 'min-content',
        textTransform: 'none',
        marginBottom: 5,
        marginTop: 4,
        borderRadius: 4,
        border: '1px solid rgba(0, 0, 0, 0.23)',
        backgroundColor: '#ffffffaa',
        '& button': {
            minHeight: 'min-content',
            textTransform: 'none',
            color: '#0B2949',
            minWidth: 100,
            fontSize: 12,
            zIndex: 1,
            fontWeight: 'bold',
            padding: '4px'
        }
    },
    indicator: {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        '& > span': {
            width: '50%',
            borderRadius: 3,
            backgroundColor: '#0B2949'
        }
    }
})((props) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

export const NewStyledTabs = withStyles({
    root: {
    },
    indicator: {
        display: 'flex',
        justifyContent: 'left',
        backgroundColor: 'transparent',
        '& > span': {
            width: '58%',
            borderRadius: 3,
            backgroundColor: '#0B2949',
            marginLeft: "10px"
        }
    }
})((props) => <Tabs variant='scrollable' scrollButtons='auto' scroll {...props} className='new-flow-tab-container' TabIndicatorProps={{ children: <span /> }} />);

export const GreenCheckbox = withStyles({
    root: {
        color: 'gray',
        '&$checked': { color: green[600] }
    },
    checked: {}
})(props =>
    <Checkbox
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        color="default" {...props}
    />
);

export const CustomNotificationCheckbox = withStyles({
    root: {
        color: '#4CAF50', // Green color for the checkbox
        '&$checked': {
            color: '#4CAF50', // Green color when checked
        },
        '& .MuiIconButton-label': {
            position: 'relative',
            zIndex: 1,
        },
        '& .MuiSvgIcon-root': {
            width: '18px', // Set the size to 12x12
            height: '18px',
        },
        padding: '2px', // Add some padding
    },
    checked: {},
})(Checkbox);

export const GreenSwitch = withStyles({
    switchBase: {
        color: '#f28b82', // Kapalı durumdaki rengi ayarlıyoruz
        '&$checked': {
            color: '#4F6E90', // Açık durumdaki rengi ayarlıyoruz
        },
        '&$checked + $track': {
            backgroundColor: '#4F6E90', // Açık durumdaki track rengi
        },
    },
    checked: {},
    track: {
        backgroundColor: '#f28b82', // Kapalı durumdaki track rengi
    },
})(Switch);
